import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import LuckyWheel from './lucky-wheel';
import WinnerDetails from './winner-details';
import SpinCount from './spin-count';
import PrizeList from './prize-list';
import TnCContainer from './tnc-container';
import FloatingLogin from '../login/desktop/FloatingLogin';
import ClaimRewardModal from '../daily-mission/claim-reward-modal';
import { portalAction } from 'root/redux/action';
import notification from '@utils/notification';
import core from 'root/utils/core';
import { gu_getContentByCurrencyLanguage, gu_getNestedValue, gu_replaceAll } from '../../../utils/general-util';
import { useHistory } from 'react-router-dom';

const getRotationAngle = (element) => {
    const matrix = window.getComputedStyle(element).getPropertyValue('transform') || 'matrix(1, 0, 0, 1, 0, 0)';
    const values = matrix.split('(')[1].split(')')[0].split(',');
    const a = parseFloat(values[0]);
    const b = parseFloat(values[1]);
    return Math.round(Math.atan2(b, a) * (180 / Math.PI));
};

const LuckyWheelLanding = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation(['lucky-wheel', 'message']);

    const screen = useSelector((state) => state.screen);
    const language = useSelector((state) => state.language);
    const user = useSelector((state) => state.user);
    const portal = useSelector((state) => state.portal);
    const winnerRef = useRef(null);

    const [state, setState] = useState({
        luckyWheelPrizes: [],
        startSpinAnimation: false,
        showLoginModal: false,
        memberEligibleSpin: 0,
        enableLuckyWheelPopup: false,
        winnerPrize: null,
        luckyWheelSettingId: '',
        isLastDepositOutOfRange: false,
        luckyWheelData: [],
        luckyWheelMinDeposit: 0,
        luckyWheelDepositDays: 0,
    });

    const {
        luckyWheelPrizes,
        startSpinAnimation,
        showLoginModal,
        memberEligibleSpin,
        enableLuckyWheelPopup,
        winnerPrize,
        luckyWheelSettingId,
        isLastDepositOutOfRange,
        luckyWheelMinDeposit,
        luckyWheelDepositDays,
    } = state;

    const history = useHistory();

    useEffect(() => {
        const fetchLuckyWheelData = async () => {
            try {
                const isLogin = user.isLogin;
                const res = await (isLogin
                    ? window.SPL_Member.getDailyMissionEnabledAndLuckyWheelDetails()
                    : window.SPL_Member.getLuckyWheelSpinSettingDetailV2(window.merchantCode, language.currencyLang));

                if (!res || (!res?.luckyWheelDetail && !res?.luckyWheelLevels) || res?.luckyWheelDetail?.luckyWheelSettingId) {
                    console.warn('Redirecting: Missing luckyWheel data');
                    history.push('/');
                    return;
                }

                const data = isLogin ? res?.luckyWheelDetail?.luckyWheelLevels?.[0] : res?.luckyWheelLevels?.[0];
                if (!data || !Array.isArray(data.luckyWheelSettingPrizes) || data.luckyWheelSettingPrizes.length === 0) {
                    console.warn('Redirecting: No prizes found');
                    history.push('/');
                    return;
                }

                const sortedPrizes = data.luckyWheelSettingPrizes.sort((a, b) => a.seq - b.seq);

                setState((prev) => ({
                    ...prev,
                    luckyWheelPrizes: sortedPrizes,
                    luckyWheelData: res?.luckyWheelDetail,
                    memberEligibleSpin: res?.luckyWheelDetail?.memberEligibleSpin || 0,
                    isLastDepositOutOfRange: res?.luckyWheelDetail?.isLastDepositOutOfRange,
                    luckyWheelSettingId: res?.luckyWheelDetail?.luckyWheelSettingId,
                }));

                core.portalIsLoading({ dispatch }, false);
            } catch (error) {
                console.error('Error fetching LuckyWheel data:', error);
                history.push('/');
            }
        };

        fetchLuckyWheelData();
    }, [user, language, dispatch]);

    const handleSpin = async () => {
        setState((prev) => ({ ...prev, showLoginModal: false }));

        if (!user.isLogin) {
            return screen.viewType === 'mobile' ? dispatch(portalAction.floatingPageUpdated('login')) : setState((prev) => ({ ...prev, showLoginModal: true }));
        }

        if (memberEligibleSpin <= 0) {
            return notification.showNotification('error', t('luckyWheel:luckyWheel.insufficientSpin'));
        }

        if (isLastDepositOutOfRange) {
            const customMsg = gu_getContentByCurrencyLanguage({}, gu_getNestedValue(portal, 'settings.features.enableLuckyWheel.customPopupMsg.isLastDepositOutOfRange'));
            const message = customMsg
                ? gu_replaceAll(gu_replaceAll(customMsg, '{{minDeposit}}', luckyWheelMinDeposit), '{{minDays}}', luckyWheelDepositDays)
                : t('luckyWheel:isLastDepositOutOfRange', {
                      minDeposit: luckyWheelMinDeposit,
                      minDays: luckyWheelDepositDays,
                  });

            return notification.showNotification('error', message);
        }

        const spinBox = document.getElementById('spinInnerWheel');
        if (spinBox) {
            spinBox.classList.remove('spin');
            spinBox.style.transition = '';
            spinBox.style.transform = '';
        }

        setState((prev) => ({ ...prev, startSpinAnimation: true }));

        try {
            const res = await window.SPL_Member.postLuckyWheelSpin(luckyWheelSettingId, language.currencyLang);
            core.portalIsLoading({ dispatch }, false);

            if (!res?.prize) {
                return res?.msg && notification.showNotification('error', t(res.msg));
            }

            const prize = res.prize;
            const prizeIndex = luckyWheelPrizes.findIndex((item) => item.seq === prize.seq);
            const segmentAngle = 360 / luckyWheelPrizes.length;
            const prizeAngle = segmentAngle * prizeIndex + segmentAngle / 2;

            const fullSpins = 360 * 5;
            const totalRotation = fullSpins + (360 - prizeAngle); // so pointer lands correctly

            spinBox.classList.add('spin');
            spinBox.style.transition = 'all 3s ease-out';
            spinBox.style.transform = `rotate(${totalRotation}deg)`;

            // Wait for spin to finish, then show result
            setTimeout(() => {
                setState((prev) => ({
                    ...prev,
                    winnerPrize: prize,
                    enableLuckyWheelPopup: true,
                    startSpinAnimation: false,
                    memberEligibleSpin: res?.memberEligibleSpin,
                }));

                if (winnerRef.current?.refreshData) {
                    winnerRef.current.refreshData();
                }
            }, 4000);
        } catch (error) {
            console.error('Spin API Error:', error);
            notification.showNotification('error', t('lucky-wheel:spinError'));
        } finally {
            setState((prev) => ({ ...prev, startSpinAnimation: false }));
        }
    };

    const showLogin = () => {
        setState((prev) => ({ ...prev, showLoginModal: true }));
    };

    const closePopup = () => {
        setState((prev) => ({ ...prev, enableLuckyWheelPopup: false, winnerPrize: null }));
    };

    const getLocalizedPrizeName = (prize) => {
        // Try to find the full prize from the list if missing langPrizeName
        const fullPrize = prize.langPrizeName ? prize : luckyWheelPrizes.find((p) => p.id === prize.id) || prize;

        let names = fullPrize.langPrizeName;

        if (typeof names === 'string') {
            try {
                names = JSON.parse(names);
            } catch {
                names = {};
            }
        }

        let normalizedLangKey = language.key.trim().toLowerCase();

        if (normalizedLangKey === 'ms') {
            normalizedLangKey = 'my';
        }

        const matchedKey = Object.keys(names || {}).find((k) => k.trim().toLowerCase() === normalizedLangKey);
        const rawName = matchedKey ? names[matchedKey] : undefined;

        return rawName?.trim() || names?.en || fullPrize.prizeName || fullPrize.reward || '';
    };

    const renderMobileLayout = () => (
        <div className='lucky-wheel-main-container'>
            <LuckyWheel onSpin={handleSpin} luckyWheelPrizes={luckyWheelPrizes} startSpinAnimation={startSpinAnimation} getLocalizedPrizeName={getLocalizedPrizeName} />
            <SpinCount memberEligibleSpin={memberEligibleSpin} />
            <PrizeList luckyWheelPrizes={luckyWheelPrizes} isMobile />
            <WinnerDetails ref={winnerRef} currencyLang={language.currencyLang} isLogin={user.isLogin} handleLoginToggle={showLogin} />
            <TnCContainer isMobile />
        </div>
    );

    const renderDesktopLayout = () => (
        <>
            <div className='lucky-wheel-main-container'>
                <LuckyWheel onSpin={handleSpin} luckyWheelPrizes={luckyWheelPrizes} startSpinAnimation={startSpinAnimation} getLocalizedPrizeName={getLocalizedPrizeName} />
                <WinnerDetails ref={winnerRef} currencyLang={language.currencyLang} isLogin={user.isLogin} handleLoginToggle={showLogin} />
                <SpinCount memberEligibleSpin={memberEligibleSpin} />
            </div>
            <PrizeList luckyWheelPrizes={luckyWheelPrizes} />
            <TnCContainer />
        </>
    );

    return (
        <div className='lucky-wheel-landing-container'>
            <div className='floating-login-wrapper'>{showLoginModal && <FloatingLogin close={() => setState((prev) => ({ ...prev, showLoginModal: false }))} />}</div>

            {enableLuckyWheelPopup && winnerPrize && (
                <ClaimRewardModal isLuckyWheel rewardName={getLocalizedPrizeName(winnerPrize)} prizeImage={winnerPrize.image} onClose={closePopup} />
            )}

            {screen.viewType === 'mobile' ? renderMobileLayout() : renderDesktopLayout()}
        </div>
    );
};

export default LuckyWheelLanding;
